import React, { useState, useEffect } from "react";
import classes from "./IncidentsPopup.module.css";
import axios from "axios";
import {
  AWS_API,
  cognitoClientId,
  cognitoUserpoolId,
} from "../../../utilities/globalVariables";

const IncidentPopup = ({
  isOpen,
  onClose,
  onSubmit,
  incidents,
  project_id,
  activity_id,
  date,
  actual_hours,
  token,
  email,
}) => {
  const INCIDENT_REGEX = /^(INC|REQ|CHG|SCTASK|RITM|PRB)\d{7}$/;

  const createRows = (count) =>
    Array.from({ length: count }, () => ({
      incident: "",
      actual_hours: "0.00",
      after_hours: "0.00",
      actual_hours_pro_rata: "0.00",
      manual: false,
      project_id,
      activity_id,
      date,
    }));

  const [rows, setRows] = useState([]);

  // Initialize rows with incidents or empty rows
  useEffect(() => {
    if (incidents && incidents.length > 0) {
      setRows(
        incidents.map((r) => ({
          ...r,
          manual: r.manual ?? false,
          actual_hours:
            r.actual_hours !== undefined ? Number(r.actual_hours).toFixed(2) : "0.00",
          after_hours:
            r.after_hours !== undefined ? Number(r.after_hours).toFixed(2) : "0.00",
          actual_hours_pro_rata:
            r.actual_hours_pro_rata !== undefined ? Number(r.actual_hours_pro_rata).toFixed(2): "0.00",
        }))
      );
    } else {
      setRows(createRows(10));
    }
  }, [incidents]);

  if (!isOpen) return null;

  const displayRows = rows.length < 10 ? [...rows, ...createRows(10 - rows.length)] : rows;

  const roundToTwo = (num) => Math.round((num + Number.EPSILON) * 100) / 100;

  const handleChange = (index, field, value) => {
    let updated = [...rows];

    while (index >= updated.length) {
      updated.push({
        incident: "",
        actual_hours: "0.00",
        after_hours: "0.00",
        actual_hours_pro_rata: "0.00",
        manual: false,
        project_id,
        activity_id,
        date,
      });
    }

    if (field === "incident") {
      const upperValue = value.toUpperCase();
      updated[index][field] = upperValue;
      updated[index].error = upperValue && !INCIDENT_REGEX.test(upperValue);
    } else {
      updated[index][field] = value === "" ? "" : value;

      const actual = parseFloat(updated[index].actual_hours) || 0;
      const after = parseFloat(updated[index].after_hours) || 0;

      updated[index].manual = actual !== 0 || after !== 0;
    }

    setRows(updated);
  };

  const handleFocus = (e) => {
    e.target.select();
  };

  const handleBlur = (index, field) => {
    let updated = [...rows];

    while (index >= updated.length) {
      updated.push({
        incident: "",
        actual_hours: "0.00",
        after_hours: "0.00",
        manual: false,
        project_id,
        activity_id,
        date,
      });
    }

    let value = updated[index][field];

    if (value === "" || value === null) {
      updated[index][field] = "0.00";
    } else {
      let numericValue = parseFloat(value);
      if (isNaN(numericValue) || numericValue < 0) numericValue = 0;

      updated[index][field] = roundToTwo(numericValue).toFixed(2);
    }

    setRows(updated);
  };

  const addRow = () => {
    setRows([
      ...rows,
      {
        incident: "",
        actual_hours: "0.00",
        after_hours: "0.00",
        actual_hours_pro_rata: "0.00",
        manual: false,
        project_id,
        activity_id,
        date,
      },
    ]);
  };

  const removeRow = (index) => {
    setRows(rows.filter((_, i) => i !== index));
  };

  const handleSubmit = async () => {
    const hasIncidentError = rows.some((row) => row.error);
    if (hasIncidentError) {
      alert("Please fix invalid incident numbers.");
      return;
    }

    const incidentValues = rows.map((row) => row.incident).filter((val) => val);
    const hasDuplicateIncidents =
      new Set(incidentValues).size !== incidentValues.length;

    if (hasDuplicateIncidents) {
      alert("Duplicate incident numbers found.");
      return;
    }

    try {
      if (!token || !email) {
        alert("Authentication missing");
        return;
      }

      const newData = {
        project_id,
        activity_id,
        date,
        actual_hours,
        rows: rows
          .filter((r) => r.incident)
          .map((r) => ({
            ...r,
                actual_hours: r.manual ? r.actual_hours : "0.00",
                after_hours: r.manual ? r.after_hours : "0.00",
                actual_hours_pro_rata: r.actual_hours_pro_rata || "0.00",
          })),
      };

      const config = {
        headers: {
          authorization: token,
          appclientid: cognitoClientId,
          userpoolid: cognitoUserpoolId,
          email: email,
        },
      };

      const res = await axios.post(
        AWS_API + "resource-data/incident-entry",
        newData,
        config
      );

      if (res.data) {
        console.log("Incident update response", res.data);
        setRows(
          res.data.rows.map((r) => ({
            ...r,
            actual_hours: Number(r.actual_hours).toFixed(2),
            after_hours: Number(r.after_hours).toFixed(2),
            actual_hours_pro_rata: Number(r.actual_hours_pro_rata).toFixed(2),
          }))
        );
        onSubmit(res.data.rows, project_id, activity_id, date);
      }

      onClose();
    } catch (err) {
      console.error(err);
      alert("Failed to submit incidents.");
    }
  };

  return (
    <div className={classes.overlay}>
      <div className={classes.modal} onClick={(e) => e.stopPropagation()}>
        <button className={classes.closeButton} onClick={onClose}>
          ✕
        </button>

        <h1>Incident Details</h1>
        <hr />

        <p className={classes.note}>
          Note: Hours will be automatically distributed to rows without manual entries.
        </p>

        <div className={classes.tableContainer}>
          <table className={classes.table}>
            <thead>
              <tr>
                <th className={classes.th}>Support Tickets</th>
                <th className={classes.th}>Actual Hours</th>
                <th className={classes.th}>After Hours</th>
                <th className={classes.th}>Pro Rata Hours</th>
                <th className={classes.th}></th>
              </tr>
            </thead>

            <tbody>
              {displayRows.map((row, index) => (
                <tr key={index}>
                  <td className={classes.td}>
                    <input
                      type="text"
                      value={row.incident}
                      onChange={(e) =>
                        handleChange(index, "incident", e.target.value)
                      }
                      style={{
                        border: row.error ? "1px solid red" : undefined,
                      }}
                      className={classes.textInput}
                    />
                    {row.error && (
                      <div style={{ color: "red", fontSize: "11px" }}>
                        Invalid format (e.g. INC1234567)
                      </div>
                    )}
                  </td>

                  <td className={`${classes.td} ${parseFloat(row.actual_hours) > 0 ? classes.highlightCell : ""}`}>
                    <input
                      type="number"
                      value={row.actual_hours}
                      onChange={(e) =>
                        handleChange(index, "actual_hours", e.target.value)
                      }
                      onBlur={() => handleBlur(index, "actual_hours")}
                      onFocus={handleFocus}
                      className={classes.numberInput}
                      step="0.01"
                    />
                  </td>

                  <td className={`${classes.td} ${parseFloat(row.after_hours) > 0 ? classes.highlightCell : ""}`}>
                    <input
                      type="number"
                      value={row.after_hours}
                      onChange={(e) =>
                        handleChange(index, "after_hours", e.target.value)
                      }
                      onBlur={() => handleBlur(index, "after_hours")}
                      onFocus={handleFocus}
                      className={classes.numberInput}
                      step="0.01"
                    />
                  </td>

                  <td className={classes.td}>
                    <input
                      type="number"
                      value={row.actual_hours_pro_rata}
                      disabled
                      className={`${classes.numberInput} ${classes.disabledInput}`}
                    />
                  </td>

                  <td className={classes.td}>
                    {index < rows.length && (
                      <button
                        className={classes.buttonDanger}
                        onClick={() => removeRow(index)}
                      >
                        ✕
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <button className={classes.addRowButton} onClick={addRow}>
          + Add Row
        </button>

        <div className={classes.expectedHours}>
          <strong>Expected: {actual_hours || 0}</strong>
        </div>

        <div className={classes.actions}>
          <button className={classes.buttonPrimary} onClick={handleSubmit}>
            Update
          </button>
        </div>
      </div>
    </div>
  );
};

export default IncidentPopup;